// A Replicable Set that implements Monotonic Views.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var _a;
// This class implements the RObject interface, and can be used as a top-level object in a Replica, or as
// a nested element inside other DAG-based RObjects.
// The set supports two modes of operation: if contentType is missing, the set is a simple set of literals.
// If contentType is present, the set works as a container for nested DAG-based RObjects. In this case, each
// time an element is added, its contents are used as the payload for the creation operation of the nested RObject's
// type. A new instance of the nested RObject is created with each Add, and its nested DAG is rooted at the position
// in the DAG where the Add operation is inserted. The returned hash for the element is the hash of the Add 
// operation, that has the creation op's payload as its content. Therefore there is no aliasing of contents: if the
// same creation payload is passed to two Add operations rooted in different DAG positions, two different nested
// elements will be created (with identical initialization params, since they have the same creation payload).
// Since the position affects the hash of the Add entry in the DAG, their hashes will be different.
// This type uses a sub-dag abstraction. When an element is added in nested mode, a sub-dag is created that will
// automatically wrap any updates to the nested type into RSet update operations, and will also automatically wrap
// any metadata and filtering parameters. When an element is read, the payload is automatically unwrapped.
// Notice that this wrapping process is not 100% transparent: some operations (like fork poistion finding) will
// return DAG entries from the outer, full DAG. So types that support being inserted as nested elements must be
// aware of this caveats.
import { json } from "@hyper-hyper-space/hhs3_json";
import { HASH_SHA256 } from "@hyper-hyper-space/hhs3_crypto";
import { dag, position } from "@hyper-hyper-space/hhs3_dag";
import { formatValidationFailure, validationOk, ValidationRejectedError, } from "@hyper-hyper-space/hhs3_mvt";
import { NestedScopedDag, RootScopedDag } from "@hyper-hyper-space/hhs3_mvt";
import { isRefAdvancePayload, extractRefVersion, prepareRefAdvance, createRefAdvanceMeta } from "@hyper-hyper-space/hhs3_mvt";
import { set } from "@hyper-hyper-space/hhs3_util";
import { createSyncSession } from "@hyper-hyper-space/hhs3_sync";
import { signPayload as signPayloadHelper } from "../../authorship.js";
import { validateRSetPayload } from "./validate.js";
import { hashElement } from "./hash.js";
import { RSetViewImpl } from "./view.js";
import { RSetDeltaAccumulator, computeRSetDelta } from "./delta.js";
import { RSET_TYPE_ID } from "./payload.js";
export const rSetFactory = {
    computeRootObjectId: (payload, ctx, _parent) => __awaiter(void 0, void 0, void 0, function* () {
        const entry = dag.createEntry(payload, {}, position(), ctx.getCrypto().hash(HASH_SHA256));
        return entry.hash;
    }),
    validateCreationPayload: (payload, _ctx, parent) => __awaiter(void 0, void 0, void 0, function* () { return validateRSetPayload(payload, { mode: 'create', parent }); }),
    executeCreationPayload: (payload, _ctx, scopedDag) => __awaiter(void 0, void 0, void 0, function* () {
        const meta = {};
        const createPayload = payload;
        if (createPayload['contentType'] === undefined) {
            meta['elmts'] = json.toSet(yield Promise.all(createPayload['initialElements'].map((e) => __awaiter(void 0, void 0, void 0, function* () { return hashElement(e); }))));
        }
        return yield scopedDag.append(createPayload, meta, position());
    }),
    loadObject: (id, ctx, opts) => __awaiter(void 0, void 0, void 0, function* () {
        var _b;
        let scopedDag;
        const parent = opts === null || opts === void 0 ? void 0 : opts.parent;
        if (parent !== undefined) {
            scopedDag = yield parent.getScopedDagForChild(id);
        }
        else {
            const backendLabel = (_b = opts === null || opts === void 0 ? void 0 : opts.backendLabel) !== null && _b !== void 0 ? _b : 'default';
            const rawDag = yield ctx.getDag(id, backendLabel);
            if (rawDag === undefined)
                throw new Error(`DAG '${id}' not found`);
            scopedDag = new RootScopedDag(rawDag);
        }
        const createOp = (yield scopedDag.loadEntry(id)).payload;
        return new RSetImpl(id, createOp, ctx, parent, opts === null || opts === void 0 ? void 0 : opts.backendLabel);
    })
};
export class RSetImpl {
    constructor(createOpId, createOp, ctx, parent, backendLabel) {
        this.meshConfig = { meshLabel: 'default' };
        this.deltaStrategy = 'bounded';
        this.createOpId = createOpId;
        this.createOp = createOp;
        this.ctx = ctx;
        this.parentObj = parent;
        this.backendLabel = backendLabel;
    }
    getBackendLabel() {
        var _b;
        if (this.parentObj !== undefined) {
            return this.parentObj.getBackendLabel();
        }
        return (_b = this.backendLabel) !== null && _b !== void 0 ? _b : 'default';
    }
    getId() {
        return this.createOpId;
    }
    getType() {
        return _a.typeId;
    }
    // Set operations
    add(element, at) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isPermissioned())
                throw new Error("Use addSigned() for permissioned sets");
            const dag = yield this.getScopedDag();
            at = at || (yield dag.getFrontier());
            const payload = yield this.createAddPayload(element, false, at);
            return this.applyValidatedPayload(payload, at);
        });
    }
    addWithBarrier(element, at) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isPermissioned())
                throw new Error("Use addWithBarrierSigned() for permissioned sets");
            const dag = yield this.getScopedDag();
            at = at || (yield dag.getFrontier());
            const payload = yield this.createAddPayload(element, true, at);
            return this.applyValidatedPayload(payload, at);
        });
    }
    createAddPayload(element, barrier, at) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.contentType() === undefined) {
                if (!this.acceptRedundantAdd()) {
                    const view = yield this.getView(at);
                    const hasElmt = yield view.has(element);
                    if (hasElmt) {
                        throw new Error("Element already exists in set, and redundant adds are not accepted");
                    }
                }
            }
            if (!this.supportBarrierAdd() && barrier) {
                throw new Error("Barrier add is not supported by this set");
            }
            if (this.supportBarrierAdd()) {
                return { action: 'add', element, barrier };
            }
            else {
                return { action: 'add', element };
            }
        });
    }
    delete(element, at) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isPermissioned())
                throw new Error("Use deleteSigned() for permissioned sets");
            if (this.contentType() !== undefined) {
                throw new Error("RSet.delete(element) is not well defined when contentType is present, please use deleteByHash");
            }
            const elementHash = yield hashElement(element);
            return this.deleteByHash(elementHash, at);
        });
    }
    deleteByHash(elementHash, at) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isPermissioned())
                throw new Error("Use deleteByHashSigned() for permissioned sets");
            const dag = yield this.getScopedDag();
            at = at || (yield dag.getFrontier());
            const payload = yield this.createDeletePayload(elementHash, false, at);
            return this.applyValidatedPayload(payload, at);
        });
    }
    deleteWithBarrier(element, at) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isPermissioned())
                throw new Error("Use deleteWithBarrierSigned() for permissioned sets");
            const elementHash = yield hashElement(element);
            return this.deleteWithBarrierByHash(elementHash, at);
        });
    }
    deleteWithBarrierByHash(elementHash, at) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isPermissioned())
                throw new Error("Use deleteWithBarrierByHashSigned() for permissioned sets");
            const dag = yield this.getScopedDag();
            at = at || (yield dag.getFrontier());
            const payload = yield this.createDeletePayload(elementHash, true, at);
            return this.applyValidatedPayload(payload, at);
        });
    }
    // Signed convenience methods for permissioned sets
    addSigned(element, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const d = yield this.getScopedDag();
            at = at || (yield d.getFrontier());
            const base = yield this.createAddPayload(element, false, at);
            const signed = yield signPayloadHelper(base, author);
            return this.applyValidatedPayload(signed, at);
        });
    }
    addWithBarrierSigned(element, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const d = yield this.getScopedDag();
            at = at || (yield d.getFrontier());
            const base = yield this.createAddPayload(element, true, at);
            const signed = yield signPayloadHelper(base, author);
            return this.applyValidatedPayload(signed, at);
        });
    }
    deleteSigned(element, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.contentType() !== undefined) {
                throw new Error("Use deleteByHashSigned for sets with contentType");
            }
            const elementHash = yield hashElement(element);
            return this.deleteByHashSigned(elementHash, author, at);
        });
    }
    deleteByHashSigned(elementHash, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const d = yield this.getScopedDag();
            at = at || (yield d.getFrontier());
            const base = yield this.createDeletePayload(elementHash, false, at);
            const signed = yield signPayloadHelper(base, author);
            return this.applyValidatedPayload(signed, at);
        });
    }
    deleteWithBarrierSigned(element, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const elementHash = yield hashElement(element);
            return this.deleteWithBarrierByHashSigned(elementHash, author, at);
        });
    }
    deleteWithBarrierByHashSigned(elementHash, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const d = yield this.getScopedDag();
            at = at || (yield d.getFrontier());
            const base = yield this.createDeletePayload(elementHash, true, at);
            const signed = yield signPayloadHelper(base, author);
            return this.applyValidatedPayload(signed, at);
        });
    }
    refAdvance(refVersion, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isPermissioned())
                throw new Error("refAdvance is only for permissioned sets");
            const d = yield this.getScopedDag();
            at = at || (yield d.getFrontier());
            const { payload: base } = prepareRefAdvance(this.capabilityRef(), refVersion);
            const signed = yield signPayloadHelper(base, author);
            return this.applyValidatedPayload(signed, at);
        });
    }
    createDeletePayload(elementHash, barrier, at) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.acceptRedundantDelete()) {
                const view = yield this.getView(at);
                const hasElmt = yield view.hasByHash(elementHash);
                if (!hasElmt) {
                    throw new Error("Element does not exist in set, and redundant deletes are not accepted");
                }
            }
            if (!this.supportBarrierDelete() && barrier) {
                throw new Error("Barrier delete is not supported by this set");
            }
            if (this.supportBarrierDelete()) {
                return { action: 'delete', elementHash, barrier };
            }
            else {
                return { action: 'delete', elementHash };
            }
        });
    }
    applyValidatedPayload(payload, at) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.selfValidate()) {
                const result = yield this.validatePayload(payload, at);
                if (!result.valid) {
                    throw new ValidationRejectedError(formatValidationFailure(result.why), result.why);
                }
            }
            return this.applyPayload(payload, at);
        });
    }
    // RObject interface
    validatePayload(payload, at) {
        return __awaiter(this, void 0, void 0, function* () {
            return validateRSetPayload(payload, { mode: 'op', set: this, at });
        });
    }
    applyPayload(payload, at) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isRefAdvancePayload(payload)) {
                const refPayload = payload;
                const meta = createRefAdvanceMeta(refPayload.refId);
                const scopedDag = yield this.getScopedDag();
                return yield scopedDag.append(payload, meta, at);
            }
            const setPayload = payload;
            if (setPayload['action'] === 'update') {
                const innerFactory = yield this.ctx.getRegistry().lookup(this.contentType());
                const innerRObject = yield this.loadChildObject(innerFactory, setPayload['elementHash']);
                return yield innerRObject.applyPayload(setPayload['updatePayload'], at);
            }
            else {
                const meta = {};
                switch (setPayload['action']) {
                    case 'create':
                        throw new Error("Create operation is not supposed to be applied _to_ a set");
                    case 'add':
                        if (this.contentType() !== undefined) {
                            const scopedDag = yield this.getScopedDag();
                            const elementHash = yield scopedDag.computeEntryHash(setPayload, at);
                            const creationDag = yield this.getCreationDagForChild(elementHash, at, setPayload);
                            const innerFactory = yield this.ctx.getRegistry().lookup(this.contentType());
                            yield innerFactory.executeCreationPayload(setPayload['element'], this.ctx, creationDag);
                            return elementHash;
                        }
                        else {
                            addMetaPropsForSetOp(setPayload, meta, yield hashElement(setPayload['element']));
                        }
                        break;
                    case 'delete':
                        addMetaPropsForSetOp(setPayload, meta, setPayload['elementHash']);
                        break;
                    default:
                        throw new Error("Invalid set action in payload: " + setPayload['action']);
                }
                const scopedDag = yield this.getScopedDag();
                return yield scopedDag.append(payload, meta, at);
            }
        });
    }
    getView(at, from) {
        return __awaiter(this, void 0, void 0, function* () {
            const dag = yield this.getScopedDag();
            at = at || (yield dag.getFrontier());
            from = from || (yield dag.getFrontier());
            return new RSetViewImpl(this, at, from);
        });
    }
    getContext() {
        return this.ctx;
    }
    configure(config) {
        this.meshConfig = Object.assign(Object.assign({}, this.meshConfig), config);
    }
    loadChildObject(innerFactory, elementHash) {
        return __awaiter(this, void 0, void 0, function* () {
            return innerFactory.loadObject(elementHash, this.ctx, { parent: this });
        });
    }
    seed() {
        return this.createOp['seed'];
    }
    contentType() {
        return this.createOp['contentType'];
    }
    acceptRedundantAdd() {
        var _b;
        return (_b = this.createOp['acceptRedundantAdd']) !== null && _b !== void 0 ? _b : true;
    }
    acceptRedundantDelete() {
        return this.createOp['acceptRedundantDelete'];
    }
    acceptUpdateForDeleted() {
        return this.createOp['acceptUpdateForDeleted'] || false;
    }
    supportBarrierAdd() {
        return this.createOp['supportBarrierAdd'] || false;
    }
    supportBarrierDelete() {
        return this.createOp['supportBarrierDelete'] || false;
    }
    isPermissioned() {
        return this.createOp['capabilityRef'] !== undefined;
    }
    capabilityRef() {
        return this.createOp['capabilityRef'];
    }
    capRequirementForAdd() {
        var _b;
        return (_b = this.createOp['capRequirements']) === null || _b === void 0 ? void 0 : _b['add'];
    }
    capRequirementForDelete() {
        var _b;
        return (_b = this.createOp['capRequirements']) === null || _b === void 0 ? void 0 : _b['delete'];
    }
    refAdvanceCaps() {
        var _b, _c;
        return (_c = (_b = this.createOp['capRequirements']) === null || _b === void 0 ? void 0 : _b['refAdvance']) !== null && _c !== void 0 ? _c : [];
    }
    refAdvanceCreators() {
        var _b, _c;
        return (_c = (_b = this.createOp['capRequirements']) === null || _b === void 0 ? void 0 : _b['refAdvanceCreators']) !== null && _c !== void 0 ? _c : true;
    }
    selfValidate() {
        return this.ctx.getConfig().selfValidate || false;
    }
    extractForeignDeps(payload, _at) {
        const ref = this.capabilityRef();
        if (ref === undefined)
            return undefined;
        if (isRefAdvancePayload(payload)) {
            const refPayload = payload;
            if (refPayload.refId !== ref)
                return undefined;
            return [{
                    dagId: ref,
                    requiredHashes: [...extractRefVersion(refPayload)],
                }];
        }
        return [{ dagId: ref, requiredHashes: [] }];
    }
    loadRCap() {
        return __awaiter(this, void 0, void 0, function* () {
            const ref = this.capabilityRef();
            if (ref === undefined)
                return undefined;
            const obj = yield this.ctx.getObject(ref);
            if (obj === undefined)
                return undefined;
            return obj;
        });
    }
    subscribe(callback) {
        throw new Error("Method not implemented.");
    }
    unsubscribe(callback) {
        throw new Error("Method not implemented.");
    }
    setDeltaStrategy(strategy) {
        this.deltaStrategy = strategy;
    }
    computeDelta(start, end) {
        return __awaiter(this, void 0, void 0, function* () {
            // computeDelta is root-only orchestration (bounds analysis + walk + compose). A
            // nested set does not lead a delta; it participates via createDeltaAccumulator.
            if (this.parentObj !== undefined) {
                throw new Error("computeDelta is not supported on nested RSet");
            }
            const rawDag = yield this.ctx.getDag(this.createOpId, this.getBackendLabel());
            if (rawDag === undefined)
                throw new Error("DAG not found");
            return computeRSetDelta(this, rawDag, this.deltaStrategy, start, end);
        });
    }
    createDeltaAccumulator(start, end) {
        return new RSetDeltaAccumulator(this, start, end);
    }
    getScopedDag() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._scopedDag === undefined) {
                if (this.parentObj !== undefined) {
                    this._scopedDag = yield this.parentObj.getScopedDagForChild(this.createOpId);
                }
                else {
                    const rawDag = yield this.ctx.getDag(this.createOpId, this.getBackendLabel());
                    if (rawDag === undefined)
                        throw new Error(`DAG '${this.createOpId}' not found`);
                    this._scopedDag = new RootScopedDag(rawDag);
                }
            }
            return this._scopedDag;
        });
    }
    getCausalDag() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._causalDag === undefined) {
                if (this.parentObj !== undefined) {
                    this._causalDag = yield this.parentObj.getCausalDag();
                }
                else {
                    const rawDag = yield this.ctx.getDag(this.createOpId, this.getBackendLabel());
                    if (rawDag === undefined)
                        throw new Error(`DAG '${this.createOpId}' not found`);
                    this._causalDag = rawDag;
                }
            }
            return this._causalDag;
        });
    }
    // NestingParent interface
    getScopedDagForChild(childId) {
        return __awaiter(this, void 0, void 0, function* () {
            const parentScopedDag = yield this.getScopedDag();
            const scope = new ElementUpdateScope(this, childId, position(childId));
            return new NestedScopedDag(parentScopedDag, scope);
        });
    }
    getCreationDagForChild(childId, at, addPayload) {
        return __awaiter(this, void 0, void 0, function* () {
            const parentScopedDag = yield this.getScopedDag();
            const scope = new ElementAddScope(this, childId, at, addPayload);
            return new NestedScopedDag(parentScopedDag, scope);
        });
    }
    // SyncableObject interface
    startSync() {
        return __awaiter(this, void 0, void 0, function* () {
            var _b;
            if (this.parentObj !== undefined)
                return;
            if (this._syncSession !== undefined)
                return;
            const mesh = this.ctx.getMesh((_b = this.meshConfig.meshLabel) !== null && _b !== void 0 ? _b : 'default');
            this._swarm = mesh.createSwarm(this.createOpId);
            const rawDag = yield this.ctx.getDag(this.createOpId, this.getBackendLabel());
            if (rawDag === undefined)
                throw new Error(`DAG '${this.createOpId}' not found`);
            const target = {
                dagId: this.createOpId,
                dag: rawDag,
                rObject: this,
                hashSuite: this.ctx.getHashSuite(),
                resolveRefDag: (refId) => __awaiter(this, void 0, void 0, function* () {
                    const label = yield this.ctx.getBackendLabel(refId);
                    return this.ctx.getDag(refId, label);
                }),
            };
            this._syncSession = createSyncSession(target, [this._swarm]);
            this._swarm.activate();
        });
    }
    stopSync() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.parentObj !== undefined)
                return;
            if (this._syncSession !== undefined) {
                this._syncSession.destroy();
                this._syncSession = undefined;
            }
            if (this._swarm !== undefined) {
                this._swarm.destroy();
                this._swarm = undefined;
            }
        });
    }
    destroy() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.stopSync();
            this._scopedDag = undefined;
            this._causalDag = undefined;
        });
    }
}
_a = RSetImpl;
RSetImpl.create = (options) => __awaiter(void 0, void 0, void 0, function* () {
    if (options.initialElements != undefined &&
        options.initialElements.length > 0 &&
        options.contentType !== undefined) {
        throw new Error("Sets of RObjects cannot have initial elements");
    }
    if (options.contentType !== undefined &&
        options.acceptRedundantAdd !== undefined) {
        throw new Error("acceptRedundantAdd cannot be set if contentType is present");
    }
    const createPayload = {
        action: 'create',
        type: RSET_TYPE_ID,
        seed: options.seed,
        initialElements: options.initialElements || [],
        hashAlgorithm: options.hashAlgorithm || 'sha256',
        acceptRedundantDelete: options.acceptRedundantDelete || false,
    };
    if (options.acceptRedundantAdd !== undefined) {
        createPayload.acceptRedundantAdd = options.acceptRedundantAdd;
    }
    if (options.supportBarrierAdd && options.supportBarrierDelete) {
        throw new Error("supportBarrierAdd and supportBarrierDelete are mutually exclusive");
    }
    if (options.supportBarrierAdd !== undefined) {
        createPayload.supportBarrierAdd = options.supportBarrierAdd;
    }
    if (options.supportBarrierDelete !== undefined) {
        createPayload.supportBarrierDelete = options.supportBarrierDelete;
    }
    if (options.contentType !== undefined) {
        createPayload['contentType'] = options.contentType;
        createPayload['acceptUpdateForDeleted'] = options.acceptRedundantUpdate || false;
    }
    if (options.parent !== undefined) {
        createPayload['parent'] = options.parent;
    }
    if (options.capabilityRef !== undefined) {
        createPayload['capabilityRef'] = options.capabilityRef;
        createPayload['capRequirements'] = options.capRequirements;
    }
    return createPayload;
});
RSetImpl.typeId = RSET_TYPE_ID;
const addMetaPropsForSetOp = (payload, meta, elmtHash) => {
    const elmts = json.toSet([elmtHash]);
    meta['elmts'] = elmts;
    if (payload['action'] === 'add' || payload['action'] === 'delete') {
        if (payload['barrier'] || false) {
            meta['barrier'] = json.toSet(['t']);
        }
    }
};
class NestedElementScope {
    constructor(parent, elementHash, startAt, executeAddOp) {
        this.parent = parent;
        this.start = startAt;
        this.elementHash = elementHash;
        this.executeAddOp = executeAddOp;
    }
    startAt() {
        return this.start;
    }
    startEmpty() {
        return this.executeAddOp !== undefined;
    }
    baseFilter() {
        return { containsValues: { "inner-elmts": [this.elementHash] } };
    }
    wrapPayload(payload, at) {
        if (this.executeAddOp !== undefined && set.eq(at, this.start)) {
            if (json.toStringNormalized(this.executeAddOp['element']) !== json.toStringNormalized(payload)) {
                throw new Error("An inner element in an RSet has modified its creation payload - this is not supported.");
            }
            return this.executeAddOp;
        }
        else {
            const outerPayload = {
                action: 'update',
                elementHash: this.elementHash,
                updatePayload: payload,
            };
            return outerPayload;
        }
    }
    unwrapPayload(payload) {
        const outerPayload = payload;
        if (outerPayload['action'] === 'update') {
            return outerPayload['updatePayload'];
        }
        else if (outerPayload['action'] === 'add') {
            return outerPayload['element'];
        }
        else {
            throw new Error("Invalid payload type in unwrapPayload for RSet: " + outerPayload['action']);
        }
    }
    wrapMeta(innerMeta, wrappedPayload, at) {
        const setPayload = wrappedPayload;
        const outerMeta = {};
        if (setPayload['action'] === 'add') {
            addMetaPropsForSetOp(setPayload, outerMeta, this.elementHash);
        }
        outerMeta["inner-elmts"] = json.toSet([this.elementHash]);
        for (const key in innerMeta) {
            outerMeta["inner-" + this.elementHash + "-" + key] = innerMeta[key];
        }
        return outerMeta;
    }
    unwrapMeta(outerMeta, wrappedPayload, at) {
        const innerMeta = {};
        const lengthToSkip = ("inner-" + this.elementHash + "-").length;
        for (const key in outerMeta) {
            if (key.startsWith("inner-" + this.elementHash + "-")) {
                innerMeta[key.substring(lengthToSkip)] = outerMeta[key];
            }
        }
        return innerMeta;
    }
    wrapFilter(filter) {
        const wrappedFilter = {};
        if (filter.containsKeys !== undefined) {
            wrappedFilter.containsKeys = filter.containsKeys.map(key => "inner-" + this.elementHash + "-" + key);
        }
        if (filter.containsValues !== undefined) {
            wrappedFilter.containsValues = {};
            for (const key in filter.containsValues) {
                wrappedFilter.containsValues['inner-' + this.elementHash + '-' + key] = filter.containsValues[key];
            }
        }
        return wrappedFilter;
    }
    validateWrappedPayload(wrappedPayload, _wrappedMeta, at) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.parent.selfValidate()) {
                return validationOk();
            }
            return this.parent.validatePayload(wrappedPayload, at);
        });
    }
}
class ElementAddScope extends NestedElementScope {
    constructor(parent, elementHash, startAt, executeAddOp) {
        super(parent, elementHash, startAt, executeAddOp);
    }
}
class ElementUpdateScope extends NestedElementScope {
    constructor(parent, elementHash, startAt) {
        super(parent, elementHash, startAt);
    }
}
export { RSetViewImpl } from "./view.js";
export * from "./delta.js";
export const RSet = RSetImpl;
export const RSetView = RSetViewImpl;
